"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Upload, Loader, Send, MessageCircle, BookOpen, GitBranch } from "lucide-react"
import { Worker, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import "katex/dist/katex.min.css"
import { postDemoAccountFileStudyBuddy } from "../postFileAws"

// Import the components for structured AI responses
import Flashcard from "./FlashCard"
import QuizQuestion from "./QuizQuestion"
import MnemonicDevice from "./MnemonicDevice"
import FlowChart from "./FlowChart"
import MindMap from "./MindMap"

// State management keys for localStorage
const STORAGE_KEYS = {
  STUDY_BUDDY_STATE: "studyBuddyState",
  CHAT_MESSAGES: "studyBuddyChatMessages",
  FRONTEND_RESPONSE_COUNT: "studyBuddyFrontendResponseCount", // NEW: Frontend-only response count
  DEMO_USED: "studyBuddyDemoUsed",
}

// IMPORTANT: In a real application, this JWT generation should happen securely on your backend.
const generateMockJwtToken = (userId = "anonUser123") => {
  const header = { alg: "HS256", typ: "JWT" }
  const payload = { userId: userId, access: "anon", iat: Math.floor(Date.now() / 1000) }
  const encodedHeader = btoa(JSON.stringify(header)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
  const encodedPayload = btoa(JSON.stringify(payload)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
  return `${encodedHeader}.${encodedPayload}.mock_signature`
}

// Global state for streaming messages
if (typeof window !== "undefined" && !window.streamingState) {
  window.streamingState = {}
}

// Helper functions for localStorage
const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.error("Error saving to localStorage:", error)
  }
}

const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error("Error reading from localStorage:", error)
    return defaultValue
  }
}

export default function LandingDemoStudyBuddy({ studyBuddyDemoUsed, setStudyBuddyDemoUsed }) {
  // State variables
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null)
  const [uploadedS3Uri, setUploadedS3Uri] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [webSocket, setWebSocket] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [inputMessage, setInputMessage] = useState("")
  const [isChatReady, setIsChatReady] = useState(false)
  const [isAiResponding, setIsAiResponding] = useState(false)
  const [selectedMode, setSelectedMode] = useState("chat")

  // NEW: Frontend-only question tracking (ignore backend)
  const [frontendResponseCount, setFrontendResponseCount] = useState(0)
  const [maxQuestions] = useState(3) // Fixed to 3, don't update from backend

  // Track if demo was used before component mounted
  const [demoUsedBeforeMount, setDemoUsedBeforeMount] = useState(false)

  const chatContainerRef = useRef(null)
  const fileInputRef = useRef(null)

  // Load state from localStorage on component mount
  useEffect(() => {
    const savedState = getFromLocalStorage(STORAGE_KEYS.STUDY_BUDDY_STATE)
    const savedMessages = getFromLocalStorage(STORAGE_KEYS.CHAT_MESSAGES, [])
    const savedFrontendResponseCount = getFromLocalStorage(STORAGE_KEYS.FRONTEND_RESPONSE_COUNT, 0)
    const savedDemoUsed = getFromLocalStorage(STORAGE_KEYS.DEMO_USED, false)

    // Set if demo was used before mounting
    setDemoUsedBeforeMount(savedDemoUsed)

    if (savedState) {
      setUploadedFileUrl(savedState.uploadedFileUrl)
      setUploadedS3Uri(savedState.uploadedS3Uri)
      setPdfPreviewUrl(savedState.pdfPreviewUrl)
      setSelectedMode(savedState.selectedMode || "chat")

      // If we have a saved S3 URI, reconnect WebSocket
      if (savedState.uploadedS3Uri) {
        connectWebSocket(savedState.uploadedS3Uri, true) // true indicates this is a reconnection
      }
    }

    // Restore chat messages
    if (savedMessages.length > 0) {
      setChatMessages(savedMessages)
    }

    // NEW: Set frontend response count from localStorage
    setFrontendResponseCount(savedFrontendResponseCount)

    // Update demo used status based on frontend count
    if (savedFrontendResponseCount >= maxQuestions) {
      setStudyBuddyDemoUsed(true)
      setDemoUsedBeforeMount(true)
    } else if (savedDemoUsed) {
      setStudyBuddyDemoUsed(true)
    }
  }, [maxQuestions, setStudyBuddyDemoUsed])

  // Save state to localStorage whenever relevant state changes
  useEffect(() => {
    const stateToSave = {
      uploadedFileUrl,
      uploadedS3Uri,
      pdfPreviewUrl,
      selectedMode,
    }

    if (uploadedFileUrl || uploadedS3Uri || pdfPreviewUrl) {
      saveToLocalStorage(STORAGE_KEYS.STUDY_BUDDY_STATE, stateToSave)
    }
  }, [uploadedFileUrl, uploadedS3Uri, pdfPreviewUrl, selectedMode])

  // Save chat messages to localStorage
  useEffect(() => {
    if (chatMessages.length > 0) {
      saveToLocalStorage(STORAGE_KEYS.CHAT_MESSAGES, chatMessages)
    }
  }, [chatMessages])

  // NEW: Save frontend response count and check demo limit
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.FRONTEND_RESPONSE_COUNT, frontendResponseCount)

    // Check if demo limit reached
    if (frontendResponseCount >= maxQuestions) {
      saveToLocalStorage(STORAGE_KEYS.DEMO_USED, true)
      setStudyBuddyDemoUsed(true)
    }
  }, [frontendResponseCount, maxQuestions, setStudyBuddyDemoUsed])

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatMessages])

  // Helper to preprocess streaming content
  const preprocessStreamingContent = useCallback((content) => {
    if (!content) return ""
    let processedContent = content
    processedContent = processedContent.replace(/\\n/g, "\n")

    if (processedContent.includes('{"response":')) {
      try {
        const responseMatch = processedContent.match(/\{"response":"(.*?)(?:"(?:,.*)?}?)?$/s)
        if (responseMatch) {
          processedContent = responseMatch[1]
          processedContent = processedContent.replace(/\\"/g, '"')
          processedContent = processedContent.replace(/\\\\/g, "\\")
        } else {
          const jsonObj = JSON.parse(processedContent)
          if (jsonObj.response) {
            processedContent = jsonObj.response
          }
        }
      } catch (e) {
        const startIndex = processedContent.indexOf('{"response":"') + 13
        if (startIndex > 12) {
          processedContent = processedContent.substring(startIndex)
          processedContent = processedContent.replace(/["}\]]*$/, "")
          processedContent = processedContent.replace(/\\"/g, '"')
          processedContent = processedContent.replace(/\\\\/g, "\\")
        }
      }
    }

    processedContent = processedContent.replace(/\\"/g, '"')
    processedContent = processedContent.replace(/\\\\/g, "\\")
    processedContent = processedContent.replace(/Follow-up Questions?:[\\s\\S]*$/i, "")
    processedContent = processedContent.replace(/\*\*Follow-up Questions?\*\*:[\s\\S]*$/i, "")
    processedContent = processedContent.trim()

    return processedContent
  }, [])

  // Helper to finalize streaming message
  const finalizeStreamingMessage = useCallback(
    (messageType, state) => {
      const finalMessage = {
        sender: "ai",
        isStreaming: false,
        isComplete: true,
        type: messageType,
      }

      let contentToUse = state.parsedObject
      if (!contentToUse && state.content) {
        try {
          if (messageType !== "chat" && typeof state.content === "string") {
            contentToUse = JSON.parse(state.content)
          } else {
            contentToUse = state.content
          }
        } catch (e) {
          console.warn(`Failed to parse JSON for ${messageType} in finalizeStreamingMessage:`, e)
          contentToUse = { front: "Error", back: ["Could not parse structured response."] }
        }
      }

      switch (messageType) {
        case "chat":
          finalMessage.text = preprocessStreamingContent(contentToUse)
          break
        case "flashcard":
          finalMessage.flashcard = contentToUse || { front: "Error", back: ["Could not parse flashcard."] }
          break
        case "quiz":
          finalMessage.questionArray = contentToUse?.questionArray || []
          break
        case "mnemonic":
          finalMessage.mnemonicDevice = contentToUse || {
            shortForm: "Error",
            explanation: "Could not parse mnemonic.",
          }
          break
        case "flowchart":
          finalMessage.flowchart = contentToUse?.flowchart
          break
        case "mindmap":
          finalMessage.mindmap = contentToUse?.mindmap
          break
        default:
          finalMessage.text = String(contentToUse)
      }

      setChatMessages((prevMessages) => {
        const updatedMessages = prevMessages.map((msg) => {
          if (msg.streamingId === state.messageId) {
            return finalMessage
          }
          return msg
        })
        return updatedMessages
      })

      delete window.streamingState[messageType]
      setIsAiResponding(false)

      // NEW: Increment frontend response count when AI response is finalized
      setFrontendResponseCount((prev) => {
        const newCount = prev + 1
        console.log(`Frontend Response Count: ${newCount}/${maxQuestions}`)

        // Show demo limit message in chat when limit reached
        if (newCount >= maxQuestions) {
          setTimeout(() => {
            setChatMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: "system",
                text: "Demo limit reached. Please login/signup for more questions.",
                type: "demo_limit",
              },
            ])
          }, 500)
        }

        return newCount
      })
    },
    [preprocessStreamingContent, maxQuestions],
  )

  // Function to connect WebSocket
  const connectWebSocket = useCallback(
    (s3Uri, isReconnection = false) => {
      if (webSocket && webSocket.readyState === 1) {
        webSocket.close()
      }

      const token = generateMockJwtToken()
      const wsUrl = `wss://t6cjk7rbea.execute-api.ap-south-1.amazonaws.com/devlopment?token=${token}`
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log("WebSocket Connected")
        setWebSocket(ws)
        const message = {
          action: "initializeSession",
          userId: "anonUser123",
          s3uri: s3Uri,
          token: generateMockJwtToken(),
        }
        ws.send(JSON.stringify(message))
      }

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data)
        console.log("Received message:", data)

        if (data.type === "streaming") {
          const { messageType, index, content, accumulatedContent } = data

          if (!window.streamingState[messageType]) {
            window.streamingState[messageType] = {
              chunks: [],
              content: "",
              isComplete: false,
              messageId: Date.now() + Math.random(),
            }

            setChatMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: "ai",
                text: "",
                isStreaming: true,
                isComplete: false,
                streamingId: window.streamingState[messageType].messageId,
                type: messageType,
                showThinkingDots: messageType !== "chat",
              },
            ])
          }

          window.streamingState[messageType].chunks.push({ index, content, isFinished: data.isComplete })
          window.streamingState[messageType].content =
            accumulatedContent || window.streamingState[messageType].content + content
          window.streamingState[messageType].isComplete = data.isComplete

          setChatMessages((prevMessages) => {
            return prevMessages.map((msg) => {
              if (msg.isStreaming && msg.streamingId === window.streamingState[messageType]?.messageId) {
                return {
                  ...msg,
                  text:
                    messageType === "chat"
                      ? preprocessStreamingContent(window.streamingState[messageType].content)
                      : "",
                  isComplete: data.isComplete,
                }
              }
              return msg
            })
          })

          setIsAiResponding(true)
        } else if (data.type === "complete") {
          const { messageType } = data
          if (window.streamingState[messageType]) {
            window.streamingState[messageType].isComplete = true

            switch (messageType) {
              case "chat":
                window.streamingState[messageType].content = data.response
                break
              case "flashcard":
                window.streamingState[messageType].parsedObject = { front: data.front, back: data.back }
                break
              case "quiz":
                window.streamingState[messageType].parsedObject = { questionArray: data.questionArray }
                break
              case "mnemonic":
                window.streamingState[messageType].parsedObject = {
                  shortForm: data.shortForm,
                  explanation: data.explanation,
                }
                break
              case "flowchart":
                window.streamingState[messageType].parsedObject = { flowchart: data.flowchart }
                break
              case "mindmap":
                window.streamingState[messageType].parsedObject = { mindmap: data.mindmap }
                break
              default:
                window.streamingState[messageType].content = data.response || JSON.stringify(data)
            }

            finalizeStreamingMessage(messageType, window.streamingState[messageType])
          } else {
            const finalMessage = { sender: "ai", isStreaming: false, isComplete: true, type: messageType }

            switch (messageType) {
              case "chat":
                finalMessage.text = data.response
                break
              case "flashcard":
                finalMessage.flashcard = { front: data.front, back: data.back }
                break
              case "quiz":
                finalMessage.questionArray = data.questionArray
                break
              case "mnemonic":
                finalMessage.mnemonicDevice = { shortForm: data.shortForm, explanation: data.explanation }
                break
              case "flowchart":
                finalMessage.flowchart = data.flowchart
                break
              case "mindmap":
                finalMessage.mindmap = data.mindmap
                break
              default:
                finalMessage.text = data.response || JSON.stringify(data)
            }

            setChatMessages((prev) => [...prev, finalMessage])
            setIsAiResponding(false)

            // NEW: Increment frontend response count for non-streaming responses too
            setFrontendResponseCount((prev) => {
              const newCount = prev + 1
              console.log(`Frontend Response Count: ${newCount}/${maxQuestions}`)

              // Show demo limit message in chat when limit reached
              if (newCount >= maxQuestions) {
                setTimeout(() => {
                  setChatMessages((prevMessages) => [
                    ...prevMessages,
                    {
                      sender: "system",
                      text: "Demo limit reached. Please login/signup for more questions.",
                      type: "demo_limit",
                    },
                  ])
                }, 500)
              }

              return newCount
            })
          }
        } else if (data.type === "session_initialized") {
          setIsChatReady(true)

          // Only add greeting message if this is not a reconnection or if no messages exist
          if (!isReconnection || chatMessages.length === 0) {
            setChatMessages((prev) => {
              // Check if greeting message already exists
              const hasGreeting = prev.some(
                (msg) =>
                  msg.sender === "ai" &&
                  msg.text === "Hello! I'm your Study Buddy. Ask me anything about the document.",
              )

              if (!hasGreeting) {
                return [
                  ...prev,
                  {
                    sender: "ai",
                    text: "Hello! I'm your Study Buddy. Ask me anything about the document.",
                    type: "chat",
                  },
                ]
              }
              return prev
            })
          }
        } else if (data.type === "question_count_update") {
          // NEW: IGNORE backend question count updates completely
          console.log("Ignoring backend question count update:", data)
        } else if (data.type === "limit_reached") {
          // NEW: IGNORE backend limit reached messages
          console.log("Ignoring backend limit reached message:", data)
        } else if (data.type === "error") {
          setIsAiResponding(false)
          setChatMessages((prev) => [
            ...prev,
            {
              sender: "system",
              text: `Error: ${data.error || data.message || "An unknown error occurred."}`,
              type: "error",
            },
          ])
        } else {
          setChatMessages((prev) => [...prev, { sender: "system", text: JSON.stringify(data), type: "info" }])
        }
      }

      ws.onclose = () => {
        console.log("WebSocket Disconnected")
        setWebSocket(null)
        setIsChatReady(false)
        setIsAiResponding(false)
        setChatMessages((prev) => [
          ...prev,
          { sender: "system", text: "Disconnected from Study Buddy. Please refresh to reconnect.", type: "error" },
        ])
      }

      ws.onerror = (error) => {
        console.error("WebSocket Error:", error)
        setChatMessages((prev) => [
          ...prev,
          { sender: "system", text: "WebSocket error. Check console for details.", type: "error" },
        ])
      }

      return () => {
        if (ws.readyState === 1) {
          ws.close()
        }
      }
    },
    [webSocket, finalizeStreamingMessage, preprocessStreamingContent, chatMessages.length, maxQuestions],
  )

  const handleFileChange = useCallback(
    async (event) => {
      // Only prevent upload if demo was used before mounting the component
      if (demoUsedBeforeMount && !pdfPreviewUrl) {
        setUploadError("You've used your free Study Buddy demo. Kindly login/signup to get more.")
        event.target.value = null
        return
      }

      const file = event.target.files[0]
      if (!file) {
        return
      }

      if (file.type !== "application/pdf") {
        setUploadError("Please upload a PDF file.")
        setPdfPreviewUrl(null)
        setUploadedFileUrl(null)
        setUploadedS3Uri(null)
        event.target.value = null
        return
      }

      setUploadError(null)
      setIsUploading(true)

      // Clear previous session data only if starting fresh
      if (!pdfPreviewUrl) {
        setChatMessages([])
        setIsChatReady(false)
        setFrontendResponseCount(0) // Reset frontend count for new session

        // Clear localStorage for new session
        localStorage.removeItem(STORAGE_KEYS.CHAT_MESSAGES)
        localStorage.removeItem(STORAGE_KEYS.STUDY_BUDDY_STATE)
        localStorage.removeItem(STORAGE_KEYS.FRONTEND_RESPONSE_COUNT)
      }

      const localPdfUrl = URL.createObjectURL(file)
      setPdfPreviewUrl(localPdfUrl)

      try {
        const uuidCreated = Date.now().toString()
        const filename = file.name
        const uploadResult = await postDemoAccountFileStudyBuddy(file, uuidCreated, filename)

        if (uploadResult && uploadResult.uri) {
          setUploadedFileUrl(uploadResult.url)
          setUploadedS3Uri(uploadResult.uri)
          connectWebSocket(uploadResult.uri)
        } else {
          setUploadError("Failed to upload file to S3.")
          setPdfPreviewUrl(null)
        }
      } catch (err) {
        console.error("Upload error:", err)
        setUploadError("An error occurred during upload.")
        setPdfPreviewUrl(null)
      } finally {
        setIsUploading(false)
        event.target.value = null
      }
    },
    [connectWebSocket, demoUsedBeforeMount, pdfPreviewUrl],
  )

  const handleSendMessage = () => {
    // NEW: Check frontend response count instead of backend
    if (
      !inputMessage.trim() ||
      !webSocket ||
      webSocket.readyState !== 1 ||
      !isChatReady ||
      isAiResponding ||
      frontendResponseCount >= maxQuestions
    ) {
      return
    }

    const userMessage = inputMessage.trim()
    setChatMessages((prev) => [...prev, { sender: "user", text: userMessage, type: "chat" }])
    setInputMessage("")
    setIsAiResponding(true)

    const message = {
      action: "askQuestion",
      question: userMessage,
      mode: selectedMode,
    }

    console.log("Sending message:", message)
    webSocket.send(JSON.stringify(message))
  }

  const triggerFileInput = () => {
    fileInputRef.current.click()
  }

  // Pre-fill input message based on selected mode
  useEffect(() => {
    switch (selectedMode) {
      case "flashcard":
        setInputMessage("Create a flashcard for: ")
        break
      case "quiz":
        setInputMessage("Create a quiz for: ")
        break
      case "mnemonic":
        setInputMessage("Help me create a mnemonic device for: ")
        break
      case "mindmap":
        setInputMessage("Create a mind map for: ")
        break
      case "flowchart":
        setInputMessage("Create a flowchart for: ")
        break
      case "chat":
      default:
        setInputMessage("")
        break
    }
  }, [selectedMode])

  // Clear all data function (for testing purposes)
  const clearAllData = () => {
    localStorage.removeItem(STORAGE_KEYS.STUDY_BUDDY_STATE)
    localStorage.removeItem(STORAGE_KEYS.CHAT_MESSAGES)
    localStorage.removeItem(STORAGE_KEYS.FRONTEND_RESPONSE_COUNT)
    localStorage.removeItem(STORAGE_KEYS.DEMO_USED)

    setUploadedFileUrl(null)
    setUploadedS3Uri(null)
    setPdfPreviewUrl(null)
    setChatMessages([])
    setFrontendResponseCount(0)
    setStudyBuddyDemoUsed(false)
    setDemoUsedBeforeMount(false)
    setIsChatReady(false)

    if (webSocket) {
      webSocket.close()
    }
  }

  // Render the demo limit reached screen ONLY if demo was used before mounting
  if (demoUsedBeforeMount && !pdfPreviewUrl) {
    return (
      <div className="max-w-md mx-auto border-2 border-gray-200 rounded-lg bg-white shadow-sm p-8 flex flex-col items-start justify-center h-[600px]">
        <h4 className="text-2xl font-bold text-gray-800 mb-4 text-left">Demo Limit Reached!</h4>
        <p className="text-red-600 text-base mb-2 text-left">
          You've used your free demo for both Study Buddy and Assignment.
        </p>
        <p className="text-gray-700 text-base mb-6 text-left">Please login/signup to continue using Padhai.</p>
        <button
          onClick={() => {
            /* Handle login/signup navigation */
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors cursor-pointer"
        >
          Login / Sign Up
        </button>
        {/* Debug button - remove in production */}
        <button
          onClick={clearAllData}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded-md text-sm"
        >
          Clear All Data (Debug)
        </button>
      </div>
    )
  }

  return (
    <>
      {isUploading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
          <h4 className="text-lg font-semibold text-gray-700">Uploading File...</h4>
          <p className="text-gray-500 text-sm">Please wait, your document is being processed.</p>
        </div>
      ) : pdfPreviewUrl ? (
        <div className="grid lg:grid-cols-2 gap-8 h-[600px]">
          {/* Left side: PDF Viewer */}
          <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden h-full">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer fileUrl={pdfPreviewUrl} />
            </Worker>
          </div>

          {/* Right side: Chat Interface */}
          <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <div ref={chatContainerRef} className="flex-1 p-4 space-y-4 overflow-y-auto">
              {chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} items-start space-x-3`}
                >
                  {msg.sender === "ai" && (
                    <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                      <img
                        src="/images/study-buddy-avatar.png?height=30&width=30"
                        alt="Study Buddy Avatar"
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    </div>
                  )}
                  <div
                    className={`p-3 rounded-xl max-w-[75%] ${
                      msg.sender === "user"
                        ? "bg-blue-600 text-white rounded-br-none"
                        : msg.type === "system"
                          ? "bg-gray-200 text-gray-700 text-sm italic"
                          : msg.type === "error"
                            ? "bg-red-100 text-red-700 text-sm italic"
                            : msg.type === "demo_limit"
                              ? "bg-orange-100 text-orange-700 text-sm font-semibold"
                              : "bg-gray-100 text-gray-800 rounded-tl-none"
                    }`}
                  >
                    {/* Render content based on message type */}
                    {msg.type === "chat" && msg.sender === "ai" && (
                      <div className="text-gray-800 [&>ol]:list-decimal [&>ol]:pl-5 [&>ul]:list-disc [&>ul]:pl-5 text-left">
                        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                    )}
                    {msg.type === "flashcard" && msg.flashcard && (
                      <Flashcard front={msg.flashcard.front} back={msg.flashcard.back} />
                    )}
                    {msg.type === "quiz" && msg.questionArray && <QuizQuestion quizData={msg.questionArray} />}
                    {msg.type === "mnemonic" && msg.mnemonicDevice && (
                      <MnemonicDevice message={{ mnemonicDevice: msg.mnemonicDevice }} />
                    )}
                    {msg.type === "flowchart" && msg.flowchart && <FlowChart message={{ flowchart: msg.flowchart }} />}
                    {msg.type === "mindmap" && msg.mindmap && <MindMap message={{ mindmap: msg.mindmap }} />}

                    {/* For user messages or system/error/demo_limit messages */}
                    {msg.sender === "user" ||
                    msg.type === "system" ||
                    msg.type === "error" ||
                    msg.type === "demo_limit" ? (
                      <p>{msg.text}</p>
                    ) : null}

                    {/* Show thinking dots for AI responses that are still streaming or loading structured content */}
                    {msg.isStreaming && msg.showThinkingDots && (
                      <span className="inline-block w-2 h-2 ml-1 bg-blue-600 rounded-full animate-pulse"></span>
                    )}
                  </div>
                  {msg.sender === "user" && (
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <img
                        src="/images/vipul-avatar.png?height=30&width=30"
                        alt="User Avatar"
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* General AI thinking dots for when a new request is sent and no streaming message is yet received */}
              {isAiResponding && chatMessages[chatMessages.length - 1]?.sender !== "ai" && (
                <div className="flex justify-start items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                    <img
                      src="/images/study-buddy-avatar.png?height=24&width=24"
                      alt="Study Buddy Avatar"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  </div>
                  <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                    <span className="inline-block w-2 h-2 ml-1 bg-blue-600 rounded-full animate-pulse"></span>
                    <span className="inline-block w-2 h-2 ml-1 bg-blue-600 rounded-full animate-pulse delay-100"></span>
                    <span className="inline-block w-2 h-2 ml-1 bg-blue-600 rounded-full animate-pulse delay-200"></span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 p-4 bg-white relative">
              {/* NEW: Show "Demo Used" based on frontend count */}
              {frontendResponseCount >= maxQuestions ? (
                <div className="text-center py-3 text-orange-600 font-semibold bg-orange-50 rounded-md">
                  Demo Used - Please login/signup for more questions
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      placeholder={
                        isChatReady && !isAiResponding
                          ? "Type your question..."
                          : "Please wait or upload a PDF to start chat"
                      }
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!isChatReady || isAiResponding}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-md transition-colors disabled:opacity-50"
                      disabled={!isChatReady || isAiResponding || !inputMessage.trim()}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Mode Selection Buttons */}
                  <div className="flex items-center space-x-1 overflow-x-auto whitespace-nowrap pb-2 px-1">
                    <button
                      onClick={() => setSelectedMode("chat")}
                      className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                        selectedMode === "chat" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                      }`}
                    >
                      <MessageCircle size={18} />
                      <span className="ml-1 text-xs hidden sm:inline">Chat</span>
                    </button>
                    <button
                      onClick={() => setSelectedMode("flashcard")}
                      className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                        selectedMode === "flashcard" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                      }`}
                    >
                      <BookOpen size={18} />
                      <span className="ml-1 text-xs hidden sm:inline">Flashcard</span>
                    </button>
                    <button
                      onClick={() => setSelectedMode("mindmap")}
                      className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                        selectedMode === "mindmap" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                      }`}
                    >
                      <GitBranch size={18} />
                      <span className="ml-1 text-xs hidden sm:inline">Mind Map</span>
                    </button>
                  </div>
                </div>
              )}
              {/* NEW: Show frontend response count */}
              {frontendResponseCount < maxQuestions && (
                <div className="absolute bottom-2 right-4 text-sm text-gray-600">
                  Questions: {frontendResponseCount}/{maxQuestions}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-md mx-auto border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors rounded-lg bg-white shadow-sm">
          <div className="p-8 text-center">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-700 mb-2">Upload File to See Demo</h4>
            <p className="text-gray-500 text-sm mb-4">Upload a document to see StudyBuddy in action</p>
            <input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={handleFileChange} />
            <label
              htmlFor="pdf-upload"
              onClick={triggerFileInput}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors cursor-pointer inline-block"
            >
              Choose File
            </label>
            {uploadError && <p className="text-red-500 text-sm mt-4">{uploadError}</p>}
          </div>
        </div>
      )}
    </>
  )
}
